import { COLORS } from '../utils/constants';

export interface Weapon {
  id: string;
  name: string;
  damage: number;
  cooldown: number;
  range: number;
  speed: number;
  color: number;
  icon: string;
  description: string;
  price: number;
  owned: boolean;
  tier: 'common' | 'rare' | 'epic' | 'legendary';
}

export const WEAPONS: Weapon[] = [
  { id: 'plasma_blade', name: '等离子刃', damage: 8, cooldown: 350, range: 40, speed: 1, color: COLORS.cyan, icon: '⚔️', description: '基础近战武器，稳定输出', price: 0, owned: true, tier: 'common' },
  { id: 'shock_hammer', name: '震荡锤', damage: 15, cooldown: 1200, range: 50, speed: 0.8, color: COLORS.yellow, icon: '🔨', description: '重击伤害高，但速度较慢', price: 500, owned: false, tier: 'rare' },
  { id: 'void_katana', name: '虚空太刀', damage: 12, cooldown: 600, range: 55, speed: 1.3, color: COLORS.purple, icon: '🗡️', description: '攻速快，范围中等', price: 1200, owned: false, tier: 'epic' },
  { id: 'arc_bow', name: '弧光弓', damage: 10, cooldown: 800, range: 200, speed: 1.5, color: COLORS.green, icon: '🏹', description: '远程攻击，可穿透敌人', price: 800, owned: false, tier: 'rare' },
  { id: 'crystal_staff', name: '水晶法杖', damage: 18, cooldown: 2000, range: 80, speed: 0.6, color: COLORS.pink, icon: '🪄', description: '范围技能，超高伤害', price: 2000, owned: false, tier: 'epic' },
  { id: 'neuro_scythe', name: '神经镰刀', damage: 22, cooldown: 1500, range: 65, speed: 1.0, color: COLORS.red, icon: '⚔️', description: '传说武器，斩杀回血', price: 5000, owned: false, tier: 'legendary' },
  { id: 'pulse_gauntlet', name: '脉冲拳套', damage: 6, cooldown: 200, range: 35, speed: 1.8, color: COLORS.cyan, icon: '🥊', description: '极速连击，伤害递增', price: 1500, owned: false, tier: 'epic' },
  { id: 'gravity_cannon', name: '引力炮', damage: 25, cooldown: 3000, range: 250, speed: 0.5, color: COLORS.purple, icon: '🔫', description: '蓄力重炮，击退效果', price: 3500, owned: false, tier: 'legendary' },
];

class WeaponSystem {
  private equippedIndex = 0;
  private weapons: Weapon[] = [];

  constructor() {
    this.load();
  }

  private load() {
    try {
      const raw = localStorage.getItem('neuro_duel_weapons');
      if (raw) {
        const saved = JSON.parse(raw) as string[];
        WEAPONS.forEach(w => { w.owned = saved.includes(w.id); });
      }
    } catch {}
    this.weapons = WEAPONS.filter(w => w.owned);
  }

  save() {
    try {
      const owned = WEAPONS.filter(w => w.owned).map(w => w.id);
      localStorage.setItem('neuro_duel_weapons', JSON.stringify(owned));
    } catch {}
  }

  getOwnedWeapons(): Weapon[] {
    return WEAPONS.filter(w => w.owned);
  }

  getAllWeapons(): Weapon[] {
    return [...WEAPONS];
  }

  buyWeapon(id: string): boolean {
    const weapon = WEAPONS.find(w => w.id === id);
    if (!weapon || weapon.owned) return false;
    weapon.owned = true;
    this.weapons = WEAPONS.filter(w => w.owned);
    this.save();
    return true;
  }

  equipWeapon(id: string): boolean {
    const idx = this.weapons.findIndex(w => w.id === id);
    if (idx >= 0) {
      this.equippedIndex = idx;
      return true;
    }
    return false;
  }

  getEquipped(): Weapon {
    if (this.weapons.length === 0) return WEAPONS[0];
    return this.weapons[this.equippedIndex] || WEAPONS[0];
  }

  switchNext(): Weapon {
    if (this.weapons.length === 0) return WEAPONS[0];
    this.equippedIndex = (this.equippedIndex + 1) % this.weapons.length;
    return this.weapons[this.equippedIndex];
  }

  switchPrev(): Weapon {
    if (this.weapons.length === 0) return WEAPONS[0];
    this.equippedIndex = (this.equippedIndex - 1 + this.weapons.length) % this.weapons.length;
    return this.weapons[this.equippedIndex];
  }

  switchById(id: string): Weapon | null {
    const idx = this.weapons.findIndex(w => w.id === id);
    if (idx >= 0) {
      this.equippedIndex = idx;
      return this.weapons[this.equippedIndex];
    }
    return null;
  }

  getWeaponList(): Weapon[] {
    return this.weapons;
  }

  reset() {
    this.weapons = WEAPONS.filter(w => w.owned);
    this.equippedIndex = 0;
  }
}

export const weaponSystem = new WeaponSystem();
